import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { ExternalLink, LogOut, Menu, X } from "lucide-react";
import { useAdmin } from "@/admin/store";

const LINKS = [
  { to: "/admin", label: "Dashboard", end: true },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/roles", label: "Roles" },
  { to: "/admin/events", label: "Events" },
  { to: "/admin/event-tags", label: "Event Tags" },
  { to: "/admin/gallery", label: "Gallery" },
  { to: "/admin/categories", label: "Categories" },
  { to: "/admin/menu", label: "Menu Items" },
];

const SidebarContent = ({ onNavigate }) => {
  const { logout } = useAdmin();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-cream/10 px-7 py-7">
        <p className="font-display text-2xl tracking-[0.18em] text-ivory">VEEDU</p>
        <p className="mt-1 font-body text-[9px] tracking-[0.5em] uppercase text-brass">Admin</p>
      </div>
      <nav className="flex-1 overflow-y-auto px-4 py-6">
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={`sidebar-${l.label.toLowerCase().replace(/ /g, "-")}`}
            onClick={onNavigate}
            className={({ isActive }) =>
              `mb-1 flex items-center gap-3 border-l-2 px-4 py-2.5 font-body text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 ${
                isActive
                  ? "border-brass text-brass"
                  : "border-transparent text-cream/55 hover:text-cream"
              }`
            }
          >
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-cream/10 px-4 py-5">
        <a
          data-testid="sidebar-view-website"
          href="/"
          className="mb-1 flex items-center gap-3 px-4 py-2.5 font-body text-[11px] tracking-[0.25em] uppercase text-cream/55 transition-colors hover:text-cream"
        >
          <ExternalLink size={13} strokeWidth={1.5} /> View Website
        </a>
        <button
          data-testid="sidebar-logout"
          onClick={() => {
            logout();
            navigate("/admin/login");
          }}
          className="flex items-center gap-3 px-4 py-2.5 font-body text-[11px] tracking-[0.25em] uppercase text-cream/55 transition-colors hover:text-terra"
        >
          <LogOut size={13} strokeWidth={1.5} /> Logout
        </button>
      </div>
    </div>
  );
};

const AdminLayout = () => {
  const { authed, loading } = useAdmin();
  const [drawer, setDrawer] = useState(false);

  if (loading) return <div className="flex h-screen items-center justify-center bg-ink text-cream">Loading...</div>;
  if (!authed) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-ink text-cream">
      <aside className="fixed inset-y-0 left-0 z-[150] hidden w-60 border-r border-cream/10 bg-[#120c07] lg:block">
        <SidebarContent />
      </aside>

      <div className="sticky top-0 z-[145] flex items-center justify-between border-b border-cream/10 bg-[#120c07] px-5 py-4 lg:hidden">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-xl tracking-[0.18em] text-ivory">VEEDU</span>
          <span className="font-body text-[8px] tracking-[0.5em] uppercase text-brass">Admin</span>
        </div>
        <button
          data-testid="admin-drawer-btn"
          onClick={() => setDrawer(true)}
          aria-label="Open navigation"
          className="text-cream/70"
        >
          <Menu size={20} strokeWidth={1.5} />
        </button>
      </div>

      <AnimatePresence>
        {drawer && (
          <motion.div
            className="fixed inset-0 z-[190] bg-ink/80 backdrop-blur-sm lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawer(false)}
          >
            <motion.aside
              className="h-full w-64 border-r border-cream/10 bg-[#120c07]"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end p-4">
                <button onClick={() => setDrawer(false)} aria-label="Close navigation" className="text-cream/60">
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
              <SidebarContent onNavigate={() => setDrawer(false)} />
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="px-5 py-8 md:px-10 md:py-12 lg:ml-60 lg:px-14">
        <div className="mx-auto max-w-5xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
